import { db } from '../config';
import { collection, doc, getDoc, setDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config';

// Types
export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
  createdAt: number;
  updatedAt: number;
}

export interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
  enabled: boolean;
  provider?: string;
  security?: string;
}

export interface TemplateVariableSchema {
  name: string;
  description: string;
  variables: { name: string; description: string }[];
}

// Define available variable schemas
const AVAILABLE_VARIABLE_SCHEMAS: TemplateVariableSchema[] = [
  {
    name: 'reservation',
    description: 'Reservation information',
    variables: [
      { name: 'id', description: 'The unique ID of the reservation' },
      { name: 'reservationId', description: 'The unique ID of the reservation' },
      { name: 'customerDetails_firstName', description: 'Customer\'s first name' },
      { name: 'customerDetails_lastName', description: 'Customer\'s last name' },
      { name: 'customerDetails_email', description: 'Customer\'s email address' },
      { name: 'vesselType', description: 'Type of vessel (pontoon, fishing boat, etc.)' },
      { name: 'date', description: 'Reservation date' },
      { name: 'startDate', description: 'Reservation start date' },
      { name: 'startTime', description: 'Reservation start time' },
      { name: 'endTime', description: 'Reservation end time' },
      { name: 'partySize', description: 'Number of people in the reservation' },
      { name: 'originalPrice', description: 'Original price before discounts' },
      { name: 'discountAmount', description: 'Amount of discount applied' },
      { name: 'damageWaiverAmount', description: 'Amount for damage waiver' },
      { name: 'taxAmount', description: 'Amount of tax' },
      { name: 'finalPrice', description: 'Final price after all adjustments' },
      { name: 'totalAmount', description: 'Total amount charged for the reservation' },
      { name: 'promoCode', description: 'Promo code used if any' },
      { name: 'payment_depositAmount', description: 'Amount of deposit paid' },
      { name: 'payment_remainingAmount', description: 'Remaining balance to be paid' },
      { name: 'payment_paymentDate', description: 'Date of payment' },
      { name: 'paymentDetails_cardBrand', description: 'Brand of card used' },
      { name: 'paymentDetails_last4', description: 'Last 4 digits of card' }
    ]
  },
  {
    name: 'customer',
    description: 'Customer information',
    variables: [
      { name: 'firstName', description: 'Customer\'s first name' },
      { name: 'lastName', description: 'Customer\'s last name' },
      { name: 'email', description: 'Customer\'s email address' },
      { name: 'phone', description: 'Customer\'s phone number' }
    ]
  },
  {
    name: 'marina',
    description: 'Marina information',
    variables: [
      { name: 'marinaName', description: 'Name of the marina' },
      { name: 'marinaAddress', description: 'Address of the marina' },
      { name: 'marinaPhone', description: 'Phone number of the marina' },
      { name: 'marinaEmail', description: 'Email address of the marina' },
      { name: 'websiteUrl', description: 'URL of the marina website' }
    ]
  }
];

// Collection references
const emailTemplatesRef = collection(db, 'emailTemplates');
const emailSettingsRef = doc(db, 'settings', 'email');


// Initialize default templates if they don't exist
export const initializeDefaultTemplates = async (): Promise<void> => {
  try {
    const existingTemplates = await getEmailTemplates();
    const existingTemplateNames = existingTemplates.map(t => t.name);

    // Create a basic reservation confirmation template if it doesn't exist
    const defaultTemplate = {
      name: 'Reservation Confirmation',
      subject: 'Reservation Confirmation for {{date}}',
      body: 
        '<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">' +
        '<h2 style="color: #2c3e50; margin-bottom: 20px;">Reservation Confirmation</h2>' +
        '<p>Dear {{customerDetails_firstName}},</p>' +
        '<p>Thank you for your reservation at {{marinaName}}. Here are your reservation details:</p>' +
        '<h3 style="color: #2c3e50; margin-top: 20px;">Reservation Information</h3>' +
        '<ul style="list-style: none; padding-left: 0;">' +
        '  <li style="margin-bottom: 10px;"><strong>Reservation ID:</strong> {{id}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Vessel Type:</strong> {{vesselType}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Date:</strong> {{date}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Party Size:</strong> {{partySize}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Time:</strong> {{startTime}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Duration:</strong> {{startTime}} - {{endTime}}</li>' +
        '</ul>' +
        '<h3 style="color: #2c3e50; margin-top: 20px;">Payment Details</h3>' +
        '<ul style="list-style: none; padding-left: 0;">' +
        '  <li style="margin-bottom: 10px;"><strong>Base Rate:</strong> ${{originalPrice}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Damage Waiver:</strong> ${{damageWaiverAmount}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Tax:</strong> ${{taxAmount}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Total Amount:</strong> ${{finalPrice}}</li>' +
        '</ul>' +
        '<h3 style="color: #2c3e50; margin-top: 20px;">Payment Information</h3>' +
        '<ul style="list-style: none; padding-left: 0;">' +
        '  <li style="margin-bottom: 10px;"><strong>Deposit Paid:</strong> ${{payment_depositAmount}}</li>' +
        '  <li style="margin-bottom: 10px;"><strong>Remaining Balance:</strong> ${{payment_remainingAmount}} +Fuel</li>' +
        '</ul>' +
        '<p style="margin-top: 20px;">Please arrive 15 minutes before your scheduled time. If you need to make any changes or have questions, please contact us.</p>' +
        '<div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">' +
        '<p style="margin: 0;">Best regards,<br>' +
        '{{marinaName}} Team<br>' +
        '{{marinaPhone}}<br>' +
        '{{marinaEmail}}</p>' +
        '</div>' +
        '</div>',
      variables: [
        'id', 
        'customerDetails_firstName', 
        'vesselType', 
        'date', 
        'startTime', 
        'endTime', 
        'partySize', 
        'originalPrice', 
        'damageWaiverAmount', 
        'taxAmount', 
        'finalPrice', 
        'payment_depositAmount', 
        'payment_remainingAmount',
        'marinaName',
        'marinaPhone',
        'marinaEmail'
      ]
    };

    if (!existingTemplateNames.includes(defaultTemplate.name)) {
      await createEmailTemplate(defaultTemplate);
    }
  } catch (error) {
    console.error('Error initializing default templates:', error);
    throw error;
  }
};

// Get email settings
export const getEmailSettings = async (): Promise<EmailSettings | null> => {
  try {
    const settingsDoc = await getDoc(emailSettingsRef);
    return settingsDoc.exists() ? settingsDoc.data() as EmailSettings : null;
  } catch (error) {
    console.error('Error getting email settings:', error);
    throw error;
  }
};

// Update email settings
export const updateEmailSettings = async (settings: Partial<EmailSettings>): Promise<void> => {
  try {
    await setDoc(emailSettingsRef, {
      ...settings,
      updatedAt: Date.now()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating email settings:', error);
    throw error;
  }
};

// Get all email templates
export const getEmailTemplates = async (): Promise<EmailTemplate[]> => {
  try {
    const templatesSnapshot = await getDocs(emailTemplatesRef);
    return templatesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as EmailTemplate[];
  } catch (error) {
    console.error('Error getting email templates:', error);
    throw error;
  }
};

// Get email template by ID
export const getEmailTemplate = async (id: string): Promise<EmailTemplate | null> => {
  try {
    const templateDoc = await getDoc(doc(emailTemplatesRef, id));
    return templateDoc.exists() ? templateDoc.data() as EmailTemplate : null;
  } catch (error) {
    console.error('Error getting email template:', error);
    throw error;
  }
};

// Create email template
export const createEmailTemplate = async (template: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<EmailTemplate> => {
  try {
    const id = crypto.randomUUID();
    const now = Date.now();
    const newTemplate: EmailTemplate = {
      ...template,
      id,
      createdAt: now,
      updatedAt: now
    };
    
    await setDoc(doc(emailTemplatesRef, id), newTemplate);
    return newTemplate;
  } catch (error) {
    console.error('Error creating email template:', error);
    throw error;
  }
};

// Update email template
export const updateEmailTemplate = async (id: string, template: Partial<EmailTemplate>): Promise<void> => {
  try {
    await updateDoc(doc(emailTemplatesRef, id), {
      ...template,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating email template:', error);
    throw error;
  }
};

// Delete email template
export const deleteEmailTemplate = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(emailTemplatesRef, id));
  } catch (error) {
    console.error('Error deleting email template:', error);
    throw error;
  }
};

// Get available variable schemas
export const getAvailableVariableSchemas = async (): Promise<TemplateVariableSchema[]> => {
  // In a more complex implementation, this could fetch schemas from Firebase
  // For now, we'll return the predefined schemas
  return AVAILABLE_VARIABLE_SCHEMAS;
};

// Send email using template
export const sendEmail = async (templateId: string, to: string, variables: Record<string, string>, reservationId?: string): Promise<boolean> => {
  try {
    const template = await getEmailTemplate(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    // Replace variables in subject and body
    let subject = template.subject;
    let body = template.body;
    
    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, value);
      body = body.replace(regex, value);
    }

    // Call Firebase Function to send email
    const sendEmailFunction = httpsCallable(functions, 'sendEmail');
    await sendEmailFunction({
      to,
      subject,
      body,
      templateId,
      reservationId
    });

    return true;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

export const sendTestEmail = async (
  toEmail: string,
  subject: string,
  content: string,
  variables: Record<string, string> = {},
  smtpSettings?: EmailSettings
): Promise<{ success: boolean; info?: any; error?: string }> => {
  try {
    let processedContent = content;
    for (const [key, value] of Object.entries(variables)) {
      processedContent = processedContent.replace(new RegExp(`{{${key}}}`, 'g'), value);
    }
    if (!smtpSettings) throw new Error('SMTP settings are required for test email');
    const sendTestEmailWithSmtp = httpsCallable(functions, 'sendTestEmailWithSmtp');
    const result: any = await sendTestEmailWithSmtp({
      ...smtpSettings,
      to: toEmail,
      subject,
      html: processedContent,
    });
    return result.data;
  } catch (error: any) {
    return { success: false, error: error.message || error.toString() };
  }
};

// Fetch a random document from a collection under a location
export const getRandomDocument = async (collectionPath: string) => {
  try {
    const snapshot = await getDocs(collection(db, collectionPath));
    const docs = snapshot.docs;
    if (docs.length === 0) return null;
    const randomDoc = docs[Math.floor(Math.random() * docs.length)];
    return { id: randomDoc.id, ...randomDoc.data() };
  } catch (error) {
    console.error('Error fetching random document:', error);
    return null;
  }
};

export const getRandomReservation = async (locationId: string) => {
  return getRandomDocument(`locations/${locationId}/reservations`);
};

export const getRandomCustomer = async (locationId: string) => {
  return getRandomDocument(`locations/${locationId}/customers`);
};

export const getRandomMarina = async (locationId: string) => {
  // If marina info is stored under the location doc itself
  try {
    const marinaDoc = await getDoc(doc(db, 'locations', locationId));
    return marinaDoc.exists() ? { id: marinaDoc.id, ...marinaDoc.data() } : null;
  } catch (error) {
    console.error('Error fetching marina info:', error);
    return null;
  }
}; 